/**
 * Ticket Summary Component
 * Displays ConnectWise ticket information and conversation thread with Natalie
 */

import { useState, useRef, useEffect } from 'react';
import MarkdownMessage from './MarkdownMessage';

/**
 * Format timestamp for display
 * @param {number} timestamp - Unix timestamp
 * @returns {string} Formatted time string
 */
function formatTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
    ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Typing indicator component
 */
function TypingIndicator() {
  return (
    <div className="message message-bot typing-indicator">
      <span></span>
      <span></span>
      <span></span>
    </div>
  );
}

export default function TicketSummary({
  ticketData,
  messages,
  hasHistory,
  loading,
  onAnalyze,
  onFollowUp,
  onNewConversation
}) {
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeQuickAction, setActiveQuickAction] = useState(null);
  const threadRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, loading]);

  if (!ticketData) {
    return (
      <div className="ticket-summary">
        <p>No ticket data available</p>
      </div>
    );
  }

  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();
    if (!followUpMessage.trim() || !onFollowUp || isSubmitting) return;

    setIsSubmitting(true);
    await onFollowUp(followUpMessage, 'follow-up');
    setFollowUpMessage('');
    setIsSubmitting(false);
  };

  const handleQuickAction = async (actionType, message) => {
    if (!onFollowUp || isSubmitting) return;

    setActiveQuickAction(actionType);
    setIsSubmitting(true);
    await onFollowUp(message, 'quick-action');
    setIsSubmitting(false);
    setActiveQuickAction(null);
  };

  const handleAnalyze = async () => {
    if (!onAnalyze || isSubmitting) return;
    setIsSubmitting(true);
    await onAnalyze();
    setIsSubmitting(false);
  };

  return (
    <div className="ticket-summary">
      {/* Message Thread - shown when there's history */}
      {hasHistory && (
        <section className="copilot-analysis">
          <div className="section-header">
            <h3>Natalie</h3>
            {onNewConversation && (
              <button
                onClick={onNewConversation}
                className="new-conversation-btn"
                title="Start a new conversation"
                disabled={isSubmitting || loading}
              >
                New Chat
              </button>
            )}
          </div>

          {/* Message Thread */}
          <div className="message-thread" ref={threadRef}>
            {messages.map((msg) => (
              <div key={msg.id} className={`message message-${msg.role}`}>
                <div className="message-content">
                  {msg.role === 'bot' ? (
                    <MarkdownMessage content={msg.content} />
                  ) : (
                    msg.content
                  )}
                </div>
                <div className="message-time">{formatTime(msg.timestamp)}</div>
              </div>
            ))}
            {loading && <TypingIndicator />}
          </div>

          {/* Quick Action Buttons */}
          <div className="quick-actions">
            <button
              onClick={() => handleQuickAction('root-causes', 'What are the potential root causes?')}
              disabled={isSubmitting || loading}
              className={`quick-action-button ${activeQuickAction === 'root-causes' ? 'active' : ''}`}
            >
              {activeQuickAction === 'root-causes' ? '...' : 'Root Causes'}
            </button>
            <button
              onClick={() => handleQuickAction('solutions', 'What are the recommended solutions or next steps?')}
              disabled={isSubmitting || loading}
              className={`quick-action-button ${activeQuickAction === 'solutions' ? 'active' : ''}`}
            >
              {activeQuickAction === 'solutions' ? '...' : 'Solutions'}
            </button>
          </div>

          {/* Follow-up Input */}
          {onFollowUp && (
            <form onSubmit={handleFollowUpSubmit} className="follow-up-form">
              <input
                type="text"
                value={followUpMessage}
                onChange={(e) => setFollowUpMessage(e.target.value)}
                placeholder="Ask a follow-up question..."
                disabled={isSubmitting || loading}
                className="follow-up-input"
              />
              <button
                type="submit"
                disabled={!followUpMessage.trim() || isSubmitting || loading}
                className="follow-up-button"
              >
                {isSubmitting ? '...' : 'Send'}
              </button>
            </form>
          )}
        </section>
      )}

      {/* No History Yet - Show Analyze Button */}
      {!hasHistory && (
        <section className="no-analysis">
          <div className="analyze-prompt">
            <h3>Natalie Ticket Analysis</h3>
            <p>Click below to summarize this ticket and chat with Natalie</p>
          </div>
          <button
            onClick={handleAnalyze}
            className="analyze-button"
            disabled={isSubmitting || loading}
          >
            {loading ? 'Analyzing...' : 'Analyze Ticket'}
          </button>
        </section>
      )}
    </div>
  );
}
